/**
 * 订单表单弹窗「订单明细」区块：明细行编辑、每日小计/合计展示与添加行
 */
import type { OrderItemDetail } from '../../../../../types';
import { SectionHeader } from '../SectionHeader';
import { inp } from '../utils';
import { roundMoney } from '../../../../../utils/paymentAmount';

interface ItemsSectionProps {
  orderItems: OrderItemDetail[];
  /** 明细逐行闸门的结果：整块只有一条汇总提示，落在「添加明细」按钮下方（与两端同位） */
  itemsError: string;
  itemsSubtotalPerDay: number;
  itemsTotal: number;
  numDays: number | null;
  addItem: () => void;
  removeItem: (idx: number) => void;
  setItem: (idx: number, field: keyof OrderItemDetail, val: string) => void;
}

export function ItemsSection({ orderItems, itemsError, itemsSubtotalPerDay, itemsTotal, numDays, addItem, removeItem, setItem }: ItemsSectionProps) {
  // 天数未知（还没选预约日期）时按单日口径显示，不乘天数
  const isMultiDay = numDays !== null && numDays > 1;
  return (
    /* ⑤ 订单明细 */
    <div>
      <SectionHeader icon="ri-list-check-3" title="订单明细" color="bg-sky-50 text-sky-500" hint="多项需求可使用明细" />
      <div className="space-y-2">
        {/* 表头 */}
        {orderItems.length > 0 && (
          <div className="grid grid-cols-12 gap-1.5 px-1 mb-1">
            <span className="col-span-4 text-xs text-gray-400">名称</span>
            <span className="col-span-3 text-xs text-gray-400">规格</span>
            <span className="col-span-2 text-xs text-gray-400 text-center">数量</span>
            <span className="col-span-2 text-xs text-gray-400 text-right">每日单价</span>
            <span className="col-span-1" />
          </div>
        )}

        {/* 明细行 */}
        {orderItems.map((item, idx) => {
          const subtotal = roundMoney(Number(item.quantity || 0) * Number(item.unitPrice || 0));
          return (
            <div key={idx} className="group relative">
              <div className="grid grid-cols-12 gap-1.5 items-center">
                <input
                  type="text"
                  value={item.itemName}
                  onChange={e => setItem(idx, 'itemName', e.target.value)}
                  placeholder="名称"
                  className={`col-span-4 ${inp()}`}
                />
                <input
                  type="text"
                  value={item.itemSpec}
                  onChange={e => setItem(idx, 'itemSpec', e.target.value)}
                  placeholder="规格"
                  className={`col-span-3 ${inp()}`}
                />
                <input
                  type="number"
                  value={item.quantity}
                  onChange={e => setItem(idx, 'quantity', e.target.value)}
                  placeholder="1"
                  min="0"
                  className={`col-span-2 ${inp()} text-center`}
                />
                <input
                  type="number"
                  value={item.unitPrice}
                  onChange={e => setItem(idx, 'unitPrice', e.target.value)}
                  placeholder="0.00"
                  min="0"
                  step="0.01"
                  className={`col-span-2 ${inp()} text-right`}
                />
                <button
                  onClick={() => removeItem(idx)}
                  className="col-span-1 w-7 h-7 flex items-center justify-center text-gray-300 hover:text-rose-400 rounded-lg cursor-pointer transition-colors mx-auto"
                >
                  <i className="ri-delete-bin-line text-sm" />
                </button>
              </div>
              {/* 小计（量纲：该行每日小计，天数在合计行统一乘） */}
              {subtotal > 0 && (
                <p className="text-xs text-gray-400 text-right pr-8 mt-0.5">
                  每日小计：<span className="text-gray-600 font-medium">¥{subtotal.toLocaleString('zh-CN', { minimumFractionDigits: 2 })}</span>
                </p>
              )}
            </div>
          );
        })}

        {/* 合计行：一整行右对齐，「每日小计 × 天数 = 合计」不拆行 */}
        {orderItems.length > 0 && itemsSubtotalPerDay > 0 && (
          <div className="pt-2 border-t border-gray-100">
            <div className="flex items-center justify-end gap-1.5 whitespace-nowrap">
              <span className="text-xs text-gray-400">{isMultiDay ? '每日小计：' : '明细合计：'}</span>
              <span className="text-xs font-medium text-gray-500">¥{(isMultiDay ? itemsSubtotalPerDay : itemsTotal).toLocaleString('zh-CN', { minimumFractionDigits: 2 })}</span>
              {isMultiDay && (
                <>
                  <span className="text-xs text-amber-500 flex items-center gap-1">
                    <i className="ri-calendar-2-line text-xs" />
                    × {numDays} 天
                  </span>
                  <span className="text-sm font-bold text-teal-600">= ¥{itemsTotal.toLocaleString('zh-CN', { minimumFractionDigits: 2 })}</span>
                </>
              )}
            </div>
          </div>
        )}

        {/* 添加按钮 */}
        <button
          onClick={addItem}
          className="w-full flex items-center justify-center gap-1.5 py-2.5 border border-dashed border-gray-200 rounded-lg text-sm text-gray-400 hover:border-teal-300 hover:text-teal-500 hover:bg-teal-50/50 transition-all cursor-pointer"
        >
          <i className="ri-add-line text-sm" />
          添加明细
        </button>

        {itemsError && <p className="text-xs text-rose-500 pt-2">{itemsError}</p>}
      </div>
    </div>
  );
}